import { Button } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SALON_ID, api } from "../lib/trpc";

const inputClass = "h-10 w-full rounded-lg border border-creme-300 bg-white px-3";

/** « @coucounails57 », « coucounails57 » ou un lien → le lien du profil. */
export function instagramUrl(saisie: string): string {
  const brut = saisie.trim();
  if (!brut) return "";
  if (/^https?:\/\//i.test(brut)) return brut;
  return `https://www.instagram.com/${brut.replace(/^@/, "").replace(/\/+$/, "")}`;
}

/** « 07 81 74 99 44 », « +33 7 81… » ou un lien wa.me → le lien wa.me. */
export function whatsappUrl(saisie: string): string {
  const brut = saisie.trim();
  if (!brut) return "";
  if (/^https?:\/\//i.test(brut)) return brut;
  let chiffres = brut.replace(/\D/g, "");
  if (chiffres.startsWith("0") && chiffres.length === 10) chiffres = `33${chiffres.slice(1)}`;
  return `https://wa.me/${chiffres}`;
}

/** « +33781749944 » → « 07 81 74 99 44 », la forme qu'on lit et qu'on dicte. */
function phoneSaisie(phone: string | null | undefined): string {
  if (!phone) return "";
  const national = phone.startsWith("+33") ? `0${phone.slice(3)}` : phone;
  return /^0\d{9}$/.test(national) ? national.replace(/(\d{2})(?=\d)/g, "$1 ") : phone;
}

/** Saisie → E.164, comme les numéros des clientes ; inchangée si étrangère au format français. */
function phoneE164(saisie: string): string | null {
  const chiffres = saisie.replace(/[^\d+]/g, "");
  if (!chiffres) return null;
  if (/^0\d{9}$/.test(chiffres)) return `+33${chiffres.slice(1)}`;
  return chiffres;
}

/** Le lien wa.me → le numéro lisible, pour l'afficher dans le champ. */
function whatsappSaisie(url: string | undefined): string {
  const chiffres = url?.match(/wa\.me\/(\d+)/)?.[1];
  if (!chiffres) return url ?? "";
  const national = chiffres.startsWith("33") ? `0${chiffres.slice(2)}` : `+${chiffres}`;
  return national.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

/**
 * Coordonnées du salon : celles du pied de page, de la section Contact, des
 * e-mails et du PDF des cartes cadeaux.
 *
 * Elles n'avaient aucun écran : l'API savait les enregistrer, mais rien ne
 * permettait de les saisir, si bien que la vitrine montrait l'adresse du jeu
 * de démonstration.
 */
export function SalonContactPanel() {
  const queryClient = useQueryClient();
  const reglages = useQuery({
    queryKey: ["salon-settings"],
    queryFn: () => api.site.salonSettings.query({ salonId: SALON_ID }),
  });

  const [draft, setDraft] = useState({
    line1: "",
    postalCode: "",
    city: "",
    phone: "",
    email: "",
    instagram: "",
    whatsapp: "",
    facebook: "",
    tiktok: "",
    google: "",
    rating: "",
    reviewCount: "",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const s = reglages.data;
    if (!s) return;
    setDraft({
      line1: s.address?.line1 ?? "",
      postalCode: s.address?.postalCode ?? "",
      city: s.address?.city ?? "",
      phone: phoneSaisie(s.phone),
      email: s.email ?? "",
      instagram: s.socialLinks?.instagram ?? "",
      whatsapp: whatsappSaisie(s.socialLinks?.whatsapp),
      facebook: s.socialLinks?.facebook ?? "",
      tiktok: s.socialLinks?.tiktok ?? "",
      google: s.socialLinks?.google ?? "",
      rating: s.googleRating ? String(s.googleRating.average).replace(".", ",") : "",
      reviewCount: s.googleRating?.count != null ? String(s.googleRating.count) : "",
    });
  }, [reglages.data]);

  const rating = reglages.data?.googleRating;
  const automatique = rating?.source === "google";

  const save = useMutation({
    mutationFn: () => {
      const note = Number.parseFloat(draft.rating.replace(",", "."));
      const avis = Number.parseInt(draft.reviewCount, 10);
      return api.site.saveSalon.mutate({
        salonId: SALON_ID,
        phone: phoneE164(draft.phone),
        email: draft.email.trim() || null,
        address: draft.line1.trim()
          ? {
              line1: draft.line1.trim(),
              postalCode: draft.postalCode.trim(),
              city: draft.city.trim(),
              country: "FR",
            }
          : null,
        socialLinks: {
          instagram: instagramUrl(draft.instagram) || undefined,
          whatsapp: whatsappUrl(draft.whatsapp) || undefined,
          facebook: draft.facebook.trim() || undefined,
          tiktok: draft.tiktok.trim() || undefined,
          google: draft.google.trim() || undefined,
        },
        // Relevée automatiquement : on n'écrase pas la note de Google.
        ...(automatique
          ? {}
          : {
              manualGoogleRating: Number.isFinite(note)
                ? { average: note, count: Number.isFinite(avis) ? avis : null }
                : null,
            }),
      });
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      queryClient.invalidateQueries({ queryKey: ["salon-settings"] });
    },
  });

  const set = (key: keyof typeof draft) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  if (reglages.isPending) return <div className="mb-10 h-40 animate-pulse rounded-xl bg-creme-200" />;

  return (
    <section className="mb-10">
      <div className="mb-3">
        <h2 className="text-xl">Coordonnées</h2>
        <p className="max-w-2xl text-sm text-encre-500">
          Affichées dans le pied de page, la section Contact, les e-mails et les cartes cadeaux.
        </p>
      </div>

      <form
        className="grid gap-5 rounded-xl bg-white p-5 shadow-soft lg:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <fieldset className="grid gap-3 sm:grid-cols-[1fr_7rem_1fr] lg:col-span-2">
          <Field label="Adresse">
            <input value={draft.line1} onChange={set("line1")} className={inputClass} placeholder="10 rue des Genévriers" />
          </Field>
          <Field label="Code postal">
            <input value={draft.postalCode} onChange={set("postalCode")} className={inputClass} inputMode="numeric" />
          </Field>
          <Field label="Ville">
            <input value={draft.city} onChange={set("city")} className={inputClass} />
          </Field>
        </fieldset>

        <Field label="Téléphone">
          <input value={draft.phone} onChange={set("phone")} type="tel" className={inputClass} placeholder="07 81 74 99 44" />
        </Field>
        <Field label="E-mail">
          <input value={draft.email} onChange={set("email")} type="email" className={inputClass} />
        </Field>

        <Field label="Instagram" hint="@pseudo ou lien du profil">
          <input value={draft.instagram} onChange={set("instagram")} className={inputClass} placeholder="@coucounails57" />
        </Field>
        <Field label="WhatsApp" hint="Le numéro suffit : le lien de discussion est créé tout seul.">
          <input value={draft.whatsapp} onChange={set("whatsapp")} type="tel" className={inputClass} placeholder="07 81 74 99 44" />
        </Field>
        <Field label="Facebook (lien)">
          <input value={draft.facebook} onChange={set("facebook")} type="url" className={inputClass} />
        </Field>
        <Field label="TikTok (lien)">
          <input value={draft.tiktok} onChange={set("tiktok")} type="url" className={inputClass} />
        </Field>

        <fieldset className="grid gap-3 rounded-lg bg-creme-50 p-4 lg:col-span-2 sm:grid-cols-[1fr_8rem_8rem]">
          <legend className="px-1 text-sm font-medium text-encre-700">Avis Google</legend>
          <Field
            label="Lien de la fiche Google"
            hint="Sur Google Maps : votre fiche → « Partager » → copier le lien."
          >
            <input value={draft.google} onChange={set("google")} type="url" className={inputClass} placeholder="https://maps.app.goo.gl/…" />
          </Field>
          <Field label="Note sur 5">
            <input
              value={draft.rating}
              onChange={set("rating")}
              inputMode="decimal"
              disabled={automatique}
              className={inputClass}
              placeholder="4,9"
            />
          </Field>
          <Field label="Nombre d'avis">
            <input
              value={draft.reviewCount}
              onChange={set("reviewCount")}
              inputMode="numeric"
              disabled={automatique}
              className={inputClass}
            />
          </Field>
          <p className="text-xs text-encre-400 sm:col-span-3">
            {automatique
              ? `Relevée automatiquement auprès de Google, le ${new Date(rating!.updatedAt).toLocaleDateString("fr-FR")}.`
              : "Saisie à la main. Elle sera remplacée par la vraie note dès que la relève des avis Google fonctionnera."}
          </p>
        </fieldset>

        {save.isError && (
          <p role="alert" className="text-sm text-grenat-700 lg:col-span-2">
            {save.error instanceof Error ? save.error.message : "Enregistrement impossible."}
          </p>
        )}

        <div className="flex items-center gap-3 lg:col-span-2">
          <Button type="submit" loading={save.isPending}>
            Enregistrer les coordonnées
          </Button>
          {saved && <span className="text-sm text-sauge-700">✓ Enregistré — visible sur le site</span>}
        </div>
      </form>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-encre-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-encre-400">{hint}</span>}
    </label>
  );
}
